// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { useEffect, useState } from 'react';
import { Button, InputBox } from '@imprivata-cloud/components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useIdleTimer } from 'react-idle-timer';
import type {
  CredentialPrompt,
  LearnCredentialPrompt,
  RootState,
  UserNotFoundPrompt,
} from '../../store/types';
import type { BannerProps } from '../auth/Banner/banner';
import {
  CredentialPromptKind,
  DirectPromptCause,
  LearnCredentialsType,
} from '../../store/types';
import {
  Card,
  CenteringContainer,
  PaddedContainer,
  UsernameField,
  UsernamePasswordForm,
} from '../../components';
import {
  clearError,
  processCapturedCredentialsAction,
  resetStateAction,
  submitDirectCredentialsAction,
} from '../../store/actions';
import {
  LEARN_CREDENTIALS_DISPLAY_TIMEOUT_SEC,
  USER_ACTIVITY_EVENTS,
} from '../../constants';
import { showWindow } from '../../agent-api';
import { ShowWindowOption } from '../../agent-api/types';
import { Banner } from '../auth/Banner/banner';
import { AuthnLayout, LayoutHeaders } from '../auth/Card/authn-layout';
import DefaultLogo from '../../assets/default_logo.svg?react';
import PasswordIcon from '../../assets/password.svg?react';
import WifiOffIcon from '../../assets/wifi-off.svg?react';
import './learn-credentials.less';

const CAPS_LOCK_TOOLTIP_KEY = 'password.caps-lock-tooltip';

const credentialTypeToFormMapper = {
  [LearnCredentialsType.DESKTOP_ACCESS]: 'form-da',
  [LearnCredentialsType.EPIC_HYPERDRIVE]: 'form-epic',
};

export type LearnCredentialsProps = {
  credentialType: LearnCredentialsType;
  prompt: CredentialPrompt;
  logoUrl?: string;
};

export const LearnCredentials = ({
  credentialType,
  prompt,
  logoUrl,
}: LearnCredentialsProps) => {
  const dispatch = useDispatch();
  const error = useSelector((state: RootState) => state.error);

  useIdleTimer({
    timeout: LEARN_CREDENTIALS_DISPLAY_TIMEOUT_SEC * 1000,
    events: USER_ACTIVITY_EVENTS,
    onIdle: () => {
      dispatch(resetStateAction(null));
    },
  });

  // keying the form by the error remounts it, which clears the typed password
  const errorKey = error
    ? `${error.status ?? 'error'}-${error.message ?? ''}`
    : 'none';

  const shownBanner: BannerProps | undefined = error
    ? {
        status: error.status ?? 'error',
        message: error.message,
        subMessage: error.subMessage,
        clearBanner: () => {
          dispatch(clearError());
        },
      }
    : undefined;

  if (
    prompt.kind === CredentialPromptKind.DIRECT &&
    prompt.cause === DirectPromptCause.OFFLINE
  ) {
    return (
      <AuthnLayout
        header={LayoutHeaders.NONE}
        renderCustomBanner={
          shownBanner && <Banner key={errorKey} {...shownBanner} />
        }
      >
        <OfflineStep key={errorKey} />
      </AuthnLayout>
    );
  }

  if (prompt.kind === CredentialPromptKind.DIRECT && prompt.username) {
    return (
      <AuthnLayout
        header={LayoutHeaders.AUTHENTICATION}
        logoUrl={logoUrl || <DefaultLogo />}
        hexImage={<PasswordIcon />}
        banner={shownBanner}
      >
        <PasswordStep key={errorKey} username={prompt.username} />
      </AuthnLayout>
    );
  }

  return (
    <CenteringContainer>
      <Card>
        {shownBanner && <Banner key={errorKey} {...shownBanner} />}
        <PaddedContainer>
          <CredentialForm
            key={errorKey}
            credentialType={credentialType}
            prompt={prompt}
          />
        </PaddedContainer>
      </Card>
    </CenteringContainer>
  );
};

const OfflineStep = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [values, setValues] = useState({ username: '', password: '' });

  useEffect(() => {
    void showWindow(ShowWindowOption.show);
  }, []);

  return (
    <form
      className="offline-step"
      data-testid="offline-step"
      onSubmit={event => {
        event.preventDefault();
        dispatch(submitDirectCredentialsAction(values));
      }}
    >
      <div className="offline-step-heading">
        <WifiOffIcon aria-hidden />
        <h3 className="offline-step-title">
          {t('learn-credentials.form-offline.title')}
        </h3>
        <p className="offline-step-info">
          {t('learn-credentials.form-offline.info')}
        </p>
      </div>
      <div className="offline-step-form">
        <label className="offline-step-field">
          {t('learn-credentials.form-offline.username-label')}
          <InputBox
            type="text"
            autoComplete="off"
            data-testid="username-input"
            size="large"
            width="100%"
            autoFocus
            value={values.username}
            onChange={event => {
              setValues({ ...values, username: event.target.value });
            }}
          />
        </label>
        <label className="offline-step-field">
          {t('learn-credentials.form-offline.password-label')}
          <InputBox
            type="password"
            data-testid="password-input"
            size="large"
            width="100%"
            value={values.password}
            onChange={event => {
              setValues({ ...values, password: event.target.value });
            }}
            capsLockTooltip={{ label: t(CAPS_LOCK_TOOLTIP_KEY) }}
          />
        </label>
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          disabled={!values.username || !values.password}
          label={t('learn-credentials.form-offline.submit-button-text')}
          data-testid="submit-button"
        />
      </div>
    </form>
  );
};

const PasswordStep = ({ username }: { username: string }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [password, setPassword] = useState('');

  return (
    <form
      className="password-step"
      data-testid="password-step"
      onSubmit={event => {
        event.preventDefault();
        dispatch(submitDirectCredentialsAction({ username, password }));
      }}
    >
      <h3 className="password-step-title">
        {t('learn-credentials.form-direct.title-pass')}
      </h3>
      <div className="password-step-form">
        <InputBox
          type="password"
          aria-label={t('learn-credentials.form-direct.password-label')}
          data-testid="password-input"
          size="large"
          width="100%"
          autoFocus
          value={password}
          onChange={event => {
            setPassword(event.target.value);
          }}
          capsLockTooltip={{ label: t(CAPS_LOCK_TOOLTIP_KEY) }}
        />
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          disabled={!password}
          label={t('learn-credentials.form-direct.continue-button-text')}
          data-testid="submit-button"
        />
      </div>
    </form>
  );
};

const CredentialForm = ({
  credentialType,
  prompt,
}: {
  credentialType: LearnCredentialsType;
  prompt: LearnCredentialPrompt | UserNotFoundPrompt;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const codingContext = useSelector(
    (state: RootState) => state.appAccessContext?.codingContext,
  );
  const learn = prompt.kind === CredentialPromptKind.LEARN ? prompt : null;
  const [values, setValues] = useState({
    username: learn?.usernameHint ?? '',
    password: '',
  });
  const usernameField = learn?.isPasswordCaptureOnly
    ? UsernameField.READ_ONLY
    : UsernameField.EDITABLE;

  const formName = learn
    ? credentialTypeToFormMapper[credentialType]
    : 'form-direct';
  const busy =
    !!learn &&
    (learn.backendVerificationInProgress || learn.agentVerificationInProgress);

  const submit = () => {
    if (!learn) {
      dispatch(submitDirectCredentialsAction(values));
    } else if (codingContext) {
      dispatch(
        processCapturedCredentialsAction.request({
          ...values,
          codingContext,
          isPasswordCaptureOnly: learn.isPasswordCaptureOnly,
        }),
      );
    }
  };

  return (
    <UsernamePasswordForm
      data-testid="learn-credentials-form"
      values={values}
      onChange={setValues}
      onSubmit={submit}
      cancel={
        learn
          ? {
              label: t('actions.cancel'),
              onClick: () => {
                dispatch(resetStateAction(null));
              },
            }
          : undefined
      }
      disabled={!values.username || !values.password || busy}
      title={t(
        usernameField === UsernameField.EDITABLE
          ? `learn-credentials.${formName}.title-cred`
          : `learn-credentials.${formName}.title-pass`,
      )}
      info={t(`learn-credentials.${formName}.info`)}
      userPrincipalName={learn?.aadUpn ?? ''}
      usernameLabel={t(`learn-credentials.${formName}.username-label`)}
      passwordLabel={t(`learn-credentials.${formName}.password-label`)}
      capsLockTooltipLabel={t(CAPS_LOCK_TOOLTIP_KEY)}
      submitButtonText={t(`learn-credentials.${formName}.submit-button-text`)}
      usernameField={usernameField}
    />
  );
};
